import { escapeHtml, sanitizeUrl, urlPerAttributo } from './escape.ts';
import type {
  Allineamento,
  Blocco,
  BloccoPercorsoEvento,
  DocumentoEmail,
  NodoTesto,
  RenderContext,
  RigaPercorso,
} from './types.ts';

/**
 * Da documento a blocchi a HTML per email.
 *
 * L'HTML e' fatto di tabelle e stili in riga, non perche' sia bello ma perche'
 * Outlook ignora i fogli di stile e gran parte del CSS moderno: flex, grid e
 * le classi non arrivano. E' il motivo per cui questo file non assomiglia al
 * resto del frontend.
 *
 * Insieme all'HTML esce sempre la versione a testo semplice. Non e' un extra:
 * una email solo-HTML e' un segnale che i filtri antispam contano contro.
 */

const LARGHEZZA = 600;

function allinea(a: Allineamento): string {
  if (a === 'centro') return 'center';
  if (a === 'destra') return 'right';
  return 'left';
}

/** Riscrive un link per farlo passare dal tracciamento dei clic. */
function linkFinale(url: string, ctx: RenderContext): string {
  const pulito = sanitizeUrl(url);
  if (!pulito) return '';
  // In anteprima i link restano quelli veri: chi sta scrivendo la mail deve
  // poterli aprire per controllarli.
  if (ctx.anteprima || !ctx.trackingBaseUrl || !ctx.firmaLink) return pulito;
  // mailto: e tel: non si tracciano: non sono pagine.
  if (!/^https?:/.test(pulito)) return pulito;
  return `${ctx.trackingBaseUrl}/${ctx.firmaLink(pulito)}?u=${encodeURIComponent(pulito)}`;
}

function nodoHtml(nodo: NodoTesto, ctx: RenderContext): string {
  switch (nodo.t) {
    case 'testo':
      return escapeHtml(nodo.v);
    case 'grassetto':
      return `<strong>${nodo.c.map((n) => nodoHtml(n, ctx)).join('')}</strong>`;
    case 'corsivo':
      return `<em>${nodo.c.map((n) => nodoHtml(n, ctx)).join('')}</em>`;
    case 'link': {
      const href = linkFinale(nodo.url, ctx);
      const dentro = nodo.c.map((n) => nodoHtml(n, ctx)).join('');
      if (!href) return dentro;
      return `<a href="${escapeHtml(href)}" style="color: ${ctx.colori.primario}; text-decoration: underline;" target="_blank" rel="noopener noreferrer">${dentro}</a>`;
    }
  }
}

function nodoTesto(nodo: NodoTesto): string {
  switch (nodo.t) {
    case 'testo':
      return nodo.v;
    case 'grassetto':
    case 'corsivo':
      return nodo.c.map(nodoTesto).join('');
    case 'link': {
      const dentro = nodo.c.map(nodoTesto).join('');
      const url = sanitizeUrl(nodo.url);
      // Nel testo semplice il link va scritto per esteso, altrimenti sparisce.
      return url && dentro !== url ? `${dentro} (${url})` : dentro || url;
    }
  }
}

const LARGHEZZE_IMMAGINE: Record<string, number> = {
  piena: LARGHEZZA - 80,
  meta: Math.round((LARGHEZZA - 80) / 2),
  piccola: 160,
};

/** L'indirizzo di un bottone a link personale in anteprima: non porta da nessuna parte. */
const SEGNAPOSTO_PERSONALE = '#';

// ---------------------------------------------------------------------------
// Percorso di un evento
// ---------------------------------------------------------------------------

export const ETICHETTA_ESEMPIO_PERCORSO = 'Esempio: qui comparirà il percorso assegnato al partecipante';
export const PERCORSO_NON_CONFERMATO = 'Il tuo percorso non è ancora stato confermato.';

/** Le righe finte dell'anteprima: chi scrive la mail vede che aspetto avra'. */
const RIGHE_ESEMPIO: RigaPercorso[] = [
  { giorno: 'Giovedì 5 novembre', orario: '14:00–15:00', aula: 'Sala A', titolo: 'Titolo della prima sessione' },
  { giorno: 'Giovedì 5 novembre', orario: '15:15–16:15', aula: 'Sala B', titolo: 'Titolo della seconda sessione' },
];

/**
 * Cosa mostrare per un blocco percorso:
 *   - righe presenti: il percorso vero del destinatario;
 *   - righe vuote: il motore ha cercato e non c'e' un percorso confermato;
 *   - righe assenti: nessuno le ha cercate. In anteprima e' il momento giusto
 *     per un esempio; in una mail vera non si mostra niente, perche' un
 *     esempio arrivato a un partecipante sembrerebbe il suo percorso.
 */
type ContenutoPercorso =
  | { tipo: 'righe'; righe: RigaPercorso[]; esempio: boolean }
  | { tipo: 'non_confermato' }
  | { tipo: 'niente' };

function contenutoPercorso(blocco: BloccoPercorsoEvento, anteprima: boolean): ContenutoPercorso {
  if (!Array.isArray(blocco.righe)) {
    return anteprima ? { tipo: 'righe', righe: RIGHE_ESEMPIO, esempio: true } : { tipo: 'niente' };
  }
  if (blocco.righe.length === 0) return { tipo: 'non_confermato' };
  return { tipo: 'righe', righe: blocco.righe, esempio: false };
}

/** Le righe raggruppate per giorno, nell'ordine in cui arrivano. */
function perGiorno(righe: RigaPercorso[]): { giorno: string; righe: RigaPercorso[] }[] {
  const gruppi: { giorno: string; righe: RigaPercorso[] }[] = [];
  for (const riga of righe) {
    const ultimo = gruppi[gruppi.length - 1];
    if (ultimo && ultimo.giorno === riga.giorno) ultimo.righe.push(riga);
    else gruppi.push({ giorno: riga.giorno, righe: [riga] });
  }
  return gruppi;
}

function percorsoHtml(blocco: BloccoPercorsoEvento, ctx: RenderContext): string {
  const contenuto = contenutoPercorso(blocco, ctx.anteprima);
  if (contenuto.tipo === 'niente') return '';
  if (contenuto.tipo === 'non_confermato') {
    return `<tr><td style="padding: 0 40px 16px 40px;">
        <p style="margin: 0; color: #374151; line-height: 1.7; font-size: 15px;">${escapeHtml(PERCORSO_NON_CONFERMATO)}</p>
      </td></tr>`;
  }

  const etichetta = contenuto.esempio
    ? `<p style="margin: 0 0 10px 0; padding: 6px 10px; border-radius: 6px; background-color: #fef3c7; color: #92400e; font-size: 12px; line-height: 1.5;">${escapeHtml(ETICHETTA_ESEMPIO_PERCORSO)}</p>`
    : '';

  // Una tabella e non dei div: e' l'unica impaginazione a colonne che
  // Outlook rispetta. Il giorno fa da intestazione, sotto una riga per
  // incontro: orario a sinistra, titolo e aula a destra.
  const giorni = perGiorno(contenuto.righe)
    .map((g) => [giornoPercorsoHtml(g.giorno, ctx), ...g.righe.map(rigaPercorsoHtml)].join('\n'))
    .join('\n');

  return `<tr><td style="padding: 0 40px 20px 40px;">
        ${etichetta}<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-collapse: collapse;">
${giorni}
        </table>
      </td></tr>`;
}

function giornoPercorsoHtml(giorno: string, ctx: RenderContext): string {
  return `<tr><td colspan="2" style="padding: 14px 0 6px 0; border-bottom: 2px solid ${ctx.colori.primario}; color: ${ctx.colori.primario}; font-size: 14px; font-weight: 700;">${escapeHtml(giorno)}</td></tr>`;
}

function rigaPercorsoHtml(r: RigaPercorso): string {
  return `<tr>
          <td valign="top" width="110" style="padding: 10px 12px 10px 0; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 14px; font-weight: 600; white-space: nowrap;">${escapeHtml(r.orario)}</td>
          <td valign="top" style="padding: 10px 0; border-bottom: 1px solid #e5e7eb; color: #374151; font-size: 14px; line-height: 1.5;"><strong style="color: #111827;">${escapeHtml(r.titolo)}</strong><br /><span style="color: #6b7280; font-size: 13px;">${escapeHtml(r.aula)}</span></td>
        </tr>`;
}

function percorsoTesto(blocco: BloccoPercorsoEvento, anteprima: boolean): string {
  const contenuto = contenutoPercorso(blocco, anteprima);
  if (contenuto.tipo === 'niente') return '';
  if (contenuto.tipo === 'non_confermato') return PERCORSO_NON_CONFERMATO;
  const righe = perGiorno(contenuto.righe).map(
    (g) => `${g.giorno}\n${g.righe.map((r) => `- ${r.orario} · ${r.aula} · ${r.titolo}`).join('\n')}`
  );
  return [contenuto.esempio ? `[${ETICHETTA_ESEMPIO_PERCORSO}]` : '', ...righe].filter(Boolean).join('\n\n');
}

function bloccoHtml(blocco: Blocco, ctx: RenderContext): string {
  switch (blocco.tipo) {
    case 'titolo': {
      const dimensioni = { 1: 26, 2: 21, 3: 17 } as const;
      return `<tr><td style="padding: 0 40px 12px 40px; text-align: ${allinea(blocco.allineamento)};">
        <h${blocco.livello} style="margin: 0; font-size: ${dimensioni[blocco.livello]}px; line-height: 1.3; color: #111827; font-weight: 700;">${escapeHtml(blocco.testo)}</h${blocco.livello}>
      </td></tr>`;
    }

    case 'testo': {
      const paragrafi = blocco.paragrafi
        .map(
          (p) =>
            `<p style="margin: 0 0 12px 0; color: #374151; line-height: 1.7; font-size: 15px;">${p
              .map((n) => nodoHtml(n, ctx))
              .join('')}</p>`
        )
        .join('');
      return `<tr><td style="padding: 0 40px 8px 40px; text-align: ${allinea(blocco.allineamento)};">${paragrafi}</td></tr>`;
    }

    case 'immagine': {
      const src = urlPerAttributo(blocco.url);
      if (!src) return '';
      const larghezza = LARGHEZZE_IMMAGINE[blocco.larghezza] ?? LARGHEZZE_IMMAGINE.piena;
      const img = `<img src="${src}" alt="${escapeHtml(blocco.alt)}" width="${larghezza}" style="display: block; width: 100%; max-width: ${larghezza}px; height: auto; border: 0;" />`;
      const href = blocco.link ? linkFinale(blocco.link, ctx) : '';
      const contenuto = href
        ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${img}</a>`
        : img;
      return `<tr><td align="center" style="padding: 0 40px 16px 40px;">${contenuto}</td></tr>`;
    }

    case 'bottone': {
      // Un bottone a link personale nel documento non ha ancora l'indirizzo:
      // lo scrive il motore per ogni destinatario. In anteprima si mostra lo
      // stesso, con un indirizzo che non porta da nessuna parte; in una mail
      // vera senza indirizzo il bottone non esce, come sempre.
      const href =
        linkFinale(blocco.url, ctx) || (blocco.personale && ctx.anteprima ? SEGNAPOSTO_PERSONALE : '');
      if (!href) return '';
      // Il bottone e' una tabella e non un <a> con padding: e' l'unico modo
      // perche' resti un rettangolo cliccabile anche in Outlook.
      return `<tr><td style="padding: 8px 40px 20px 40px;" align="${allinea(blocco.allineamento)}">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0">
          <tr><td style="background-color: ${ctx.colori.primario}; border-radius: 8px;">
            <a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" style="display: inline-block; padding: 13px 28px; font-size: 15px; font-weight: 600; color: #ffffff; text-decoration: none;">${escapeHtml(blocco.testo)}</a>
          </td></tr>
        </table>
      </td></tr>`;
    }

    case 'separatore':
      return `<tr><td style="padding: 8px 40px 20px 40px;">
        <div style="height: 1px; background-color: #e5e7eb; line-height: 1px;">&nbsp;</div>
      </td></tr>`;

    case 'spazio':
      return `<tr><td style="height: ${blocco.altezza}px; line-height: ${blocco.altezza}px; font-size: 0;">&nbsp;</td></tr>`;

    case 'percorso_evento':
      return percorsoHtml(blocco, ctx);
  }
}

function bloccoTesto(blocco: Blocco, anteprima: boolean): string {
  switch (blocco.tipo) {
    case 'titolo':
      return `${blocco.testo}\n${'='.repeat(Math.min(blocco.testo.length, 40))}`;
    case 'testo':
      return blocco.paragrafi.map((p) => p.map(nodoTesto).join('')).join('\n\n');
    case 'immagine':
      return blocco.alt ? `[${blocco.alt}]` : '';
    case 'bottone': {
      const url = sanitizeUrl(blocco.url);
      return url ? `${blocco.testo}: ${url}` : blocco.testo;
    }
    case 'separatore':
      return '---';
    case 'spazio':
      return '';
    case 'percorso_evento':
      return percorsoTesto(blocco, anteprima);
  }
}

/** Il corpo della email, senza intestazione ne' piede. */
export function renderBlocchi(documento: DocumentoEmail, ctx: RenderContext): string {
  return documento.blocchi.map((b) => bloccoHtml(b, ctx)).join('\n');
}

export function renderBlocchiTesto(documento: DocumentoEmail, ctx?: Pick<RenderContext, 'anteprima'>): string {
  const anteprima = ctx?.anteprima ?? false;
  return documento.blocchi
    .map((b) => bloccoTesto(b, anteprima))
    .filter((t) => t !== '')
    .join('\n\n');
}

/** Documento vuoto, usato quando si crea una campagna nuova. */
export function documentoVuoto(): DocumentoEmail {
  return { versione: 1, blocchi: [] };
}

/**
 * Tutti gli indirizzi cliccabili presenti in un documento.
 *
 * Serve al tracciamento dei clic: la firma di ogni link si calcola con
 * un'operazione crittografica, che e' asincrona, mentre il renderer deve
 * restare una funzione sincrona (e' la stessa che gira nel browser per
 * l'anteprima). Si raccolgono prima gli URL, si firmano, e poi il renderer
 * legge le firme gia' pronte.
 */
export function raccogliUrl(documento: DocumentoEmail): string[] {
  const trovati = new Set<string>();

  const daNodo = (nodo: NodoTesto): void => {
    if (nodo.t === 'link') {
      const pulito = sanitizeUrl(nodo.url);
      if (pulito && /^https?:/.test(pulito)) trovati.add(pulito);
      return;
    }
    if (nodo.t === 'grassetto' || nodo.t === 'corsivo') nodo.c.forEach(daNodo);
  };

  for (const blocco of documento.blocchi) {
    if (blocco.tipo === 'testo') blocco.paragrafi.forEach((p) => p.forEach(daNodo));
    if (blocco.tipo === 'bottone' || (blocco.tipo === 'immagine' && blocco.link)) {
      const grezzo = blocco.tipo === 'bottone' ? blocco.url : blocco.link!;
      const pulito = sanitizeUrl(grezzo);
      if (pulito && /^https?:/.test(pulito)) trovati.add(pulito);
    }
  }

  return [...trovati];
}
